import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { toast } from 'react-hot-toast'
import { findCurrencyPrecision, formatFloat, formatMoney, tr } from 'mmfintech-commons'
import { LabelValue, useCryptoCurrencyNotes, useFetchSessionRequest, usePolling } from 'mmfintech-checkout-commons'

import { QRCodeSVG } from 'qrcode.react'
import {
  CryptoPreviewTitle,
  CryptoPreviewWrapper,
  WarningMessageTitle,
  WarningMessageWrapper
} from './PreviewCrypto.styled'

import type { CryptoNotesDetails } from 'mmfintech-checkout-commons/types'

interface PreviewCryptoProps {
  sessionId?: string
  data?: any
}

const STATUS_POLLING_TIMEOUT = 30_000 // 30 seconds

export const PreviewCrypto = ({ sessionId, data }: PreviewCryptoProps) => {
  const { address, currency, tag, processingAmount } = data || {}

  const history = useHistory()

  const notes = useCryptoCurrencyNotes(currency, true)

  const polling = usePolling(() => reloadSessionStatus())
  const { fetchSession } = useFetchSessionRequest()

  const handleStatusFetchSuccess = (response: any): void => {
    const { status } = response?.data || {}

    switch (status) {
      case 'PROCESSED':
      case 'CONFIRMED':
        history.push('/success')
        break

      case 'FAILED':
      case 'CANCELLED':
        history.push('/fail')
        break

      default:
        polling.start(STATUS_POLLING_TIMEOUT)
    }
  }

  const handleStatusFetchFail = () => {
    polling.start(STATUS_POLLING_TIMEOUT)
  }

  const reloadSessionStatus = () => {
    fetchSession(sessionId, handleStatusFetchSuccess, handleStatusFetchFail)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: start polling on mount
  useEffect(() => {
    if (sessionId) {
      polling.start(STATUS_POLLING_TIMEOUT)
    }
  }, [])

  return (
    <>
      <CryptoPreviewWrapper>
        <CryptoPreviewTitle>{tr('CHECKOUT.CRYPTO_DETAILS.TITLE', 'Details')}</CryptoPreviewTitle>

        <LabelValue
          label={tr('CHECKOUT.CRYPTO_DETAILS.ADDRESS', 'Deposit address')}
          value={address}
          enableCopy={true}
          dataTest='address'
          onSuccess={() => {
            toast.remove()
            toast.success(tr('CHECKOUT.CRYPTO_DETAILS.ADDRESS_COPIED', 'Address is copied to clipboard'))
          }}
        />

        <div className='text-center code bordered mb-2 mt-4'>
          <QRCodeSVG value={address} size={150} />
        </div>

        <LabelValue
          label={tr('CHECKOUT.CRYPTO_DETAILS.TAG', 'Tag')}
          value={tag}
          dataTest='tag'
          enableCopy={true}
          onSuccess={() => {
            toast.remove()
            toast.success(tr('CHECKOUT.CRYPTO_DETAILS.TAG_COPIED', 'Tag is copied to clipboard'))
          }}
        />

        <LabelValue
          label={tr('CHECKOUT.CRYPTO_DETAILS.AMOUNT', 'Amount')}
          value={formatMoney(processingAmount, currency)}
          dataTest='amount'
          enableCopy={true}
          copyValue={formatFloat(processingAmount, findCurrencyPrecision(currency))}
          onSuccess={() => {
            toast.remove()
            toast.success(tr('CHECKOUT.CRYPTO_DETAILS.AMOUNT_COPIED', 'Amount is copied to clipboard'))
          }}
        />
      </CryptoPreviewWrapper>

      <WarningMessageWrapper>
        <WarningMessageTitle>{tr('CHECKOUT.WARNING_MESSAGE.PLEASE_NOTE', 'Please note')}</WarningMessageTitle>

        {notes.map((line: CryptoNotesDetails) => (
          <p key={line.keyName}>{tr(line.keyName, line.defaultValue)}</p>
        ))}
      </WarningMessageWrapper>
    </>
  )
}
